import { addMonths, format } from "date-fns";

export type GenerateInterestParams = {
  readonly startAmount?: number;
  readonly monthlyContributionAmount: number;
  readonly durationInMonths: number;
  readonly interestRate?: number;
  readonly inflationRate?: number;
};

type GenerateInterestToTargetParams = {
  readonly startAmount?: number;
  readonly targetAmount?: number;
  readonly monthlyContributionAmount: number;
  readonly interestRate?: number;
  readonly inflationRate?: number;
};

export type CalculateMonthlyContributionParams = {
  readonly targetAmount: number;
  readonly durationInMonths: number;
  readonly interestRate: number;
  readonly inflationRate: number;
  readonly newFeeRate?: number;
};

export const CHART_PADDING_TOP_COEFICIENT = 1.1;

export const getLabels = (lengthInMonths: number) => {
  const padding = Math.floor(lengthInMonths * 0.05);
  const today = new Date();

  return new Array(lengthInMonths + padding).fill("").map((_, index) => {
    return format(addMonths(today, index), "yyyy-MM-dd");
  });
};

export const generateCompoundInterestData = ({
  startAmount = 0,
  monthlyContributionAmount = 0,
  durationInMonths = 0,
  interestRate = 0,
  inflationRate = 0,
}: GenerateInterestParams): number[] => {
  const data = startAmount ? [] : [0];
  const compoundRate = interestRate - inflationRate;
  let subtotal = startAmount;

  for (let i = 1; i <= durationInMonths; i++) {
    subtotal = subtotal * (1 + compoundRate / 100 / 12);
    subtotal += monthlyContributionAmount;

    data.push(subtotal);
  }

  return data;
};

export const generateCompoundInterestDataToTarget = ({
  startAmount = 0,
  targetAmount = 0,
  monthlyContributionAmount,
  interestRate = 0,
  inflationRate = 0,
}: GenerateInterestToTargetParams): number[] => {
  const data = startAmount === 0 ? [0] : [];
  const compoundRate = interestRate - inflationRate;

  let subtotal = startAmount;
  let iterationCount = 0;
  const maxIterations = 1000;

  do {
    subtotal = subtotal * (1 + compoundRate / 100 / 12);
    subtotal += monthlyContributionAmount;

    data.push(subtotal);
    iterationCount++;

    if (iterationCount > maxIterations) {
      break;
    }
  } while (subtotal < targetAmount);

  return data;
};

export const calculateTargetAmount = (monthlyContributionAmount: number, years: number, interestRate: number, inflationRate: number) => {
  const monthlyEffectiveCompoundRate = calculateMonthlyEffectiveCompoundRate(interestRate, inflationRate);
  const months = years * 12;

  if (isApproximatelyZero(monthlyEffectiveCompoundRate)) {
    return monthlyContributionAmount * months;
  }

  const denominator = monthlyEffectiveCompoundRate;
  const numerator = (1 + monthlyEffectiveCompoundRate) ** months - 1;

  if (isApproximatelyZero(denominator)) {
    return monthlyContributionAmount * months;
  }

  const result = monthlyContributionAmount * (numerator / denominator);
  return result;
};

export const calculateMonthlyContribution = ({
  targetAmount,
  durationInMonths,
  interestRate = 0,
  inflationRate = 0,
}: CalculateMonthlyContributionParams): number => {
  if (durationInMonths === 0) return targetAmount;

  const compoundRate = (interestRate - inflationRate) / 100 / 12;

  if (isApproximatelyZero(compoundRate)) {
    return targetAmount / durationInMonths;
  }

  const denominator = (-1 + (1 + compoundRate) ** durationInMonths) / compoundRate;

  if (isApproximatelyZero(denominator)) {
    return targetAmount / durationInMonths;
  }

  const monthlyContribution = targetAmount / denominator;

  return monthlyContribution;
};

export const calculateMonthlyContributionWithFee = (monthlyContribution: number, feeRate: number): number => {
  return monthlyContribution * (1 + feeRate / 100);
};

export const calculateMonthlyContributionWithoutFee = (monthlyContributionWithFee: number, feeRate: number): number => {
  return monthlyContributionWithFee / (1 + feeRate / 100);
};

export const calculateMonthlyEffectiveCompoundRate = (interestRate: number, inflationRate: number) => {
  return (Number(interestRate) - Number(inflationRate)) / 100 / 12;
};

// Compound-interest formulas divide by a rate/denominator that is conceptually zero whenever two rates in the
// calculation are equal (e.g. interestRate === inflationRate), but floating-point arithmetic rarely lands exactly
// on 0 — so an exact `=== 0` check is unreliable. Treat anything within `epsilon` of zero as zero instead, and let
// the caller fall back to that formula's zero-rate limit (typically a plain linear calculation) rather than divide.
export const isApproximatelyZero = (value: number, epsilon = 1e-10): boolean => {
  return Math.abs(value) < epsilon;
};
